/**
 * CardcomConfigUpdater — create / edit a Cardcom webhook configuration (side drawer).
 *
 * Sections
 *   מתי זה פועל              match type (payment page by Custom19 / item by SKU — fixed after
 *                            creation), the number / SKU, description
 *   מה קורה בתשלום מוצלח     tags added to the customer, status for NEW customers only,
 *                            Schooler course enrollment, tracking event
 *   active switch + "how it works" (collapsed)
 *
 * Save (unchanged): createCardcomConfig / updateCardcomConfig with
 *   { numberId, matchType, description, tagId: null, tagIds, statusId, isActive, trackingEventType, schoolerCourse }
 * Courses: getSchoolerCourses. Props: flag 'add' | 'update', config, onClose(refresh)
 */
import React, { useState, useEffect, useRef } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import * as actionsCreators from './../../../actions/index.js';
import {
  EditDrawer,
  Section,
  Field,
  FormGrid,
  Button,
  Input,
  Select,
  Switch,
  Segmented,
  StatusBadge,
  LIST,
  useLookupList,
  toOptions,
  itemLabel,
  statusColor,
} from '../../../ui';
import classes from './CardcomConfigUpdater.css';

const emptyForm = {
  numberId: '',
  matchType: 'page',
  description: '',
  tags: [],
  statusId: null,
  statusName: '',
  isActive: true,
  trackingEventType: '',
  schoolerCourse: '',
};

const CardcomConfigUpdater = (props) => {
  const isAdd = props.flag === 'add';
  const [open, setOpen] = useState(true);
  const refreshRef = useRef(false);
  const [form, setForm] = useState(emptyForm);
  const [initialJson, setInitialJson] = useState(JSON.stringify(emptyForm));
  const [courses, setCourses] = useState([]);
  const [saving, setSaving] = useState(false);
  const tagList = useLookupList(LIST.TAG);
  const statusList = useLookupList(LIST.STATUS);

  useEffect(() => {
    props.getSchoolerCourses().then(setCourses).catch(() => setCourses([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (props.flag === 'update' && props.config) {
      const loaded = {
        numberId: props.config.numberId || '',
        matchType: props.config.matchType || 'page',
        description: props.config.description || '',
        tags: props.config.tags || [],
        statusId: props.config.statusId || null,
        statusName: (props.config.status && props.config.status.name) || '',
        isActive: props.config.isActive !== undefined ? props.config.isActive : true,
        trackingEventType: props.config.trackingEventType || '',
        schoolerCourse: props.config.schoolerCourse || '',
      };
      setForm(loaded);
      setInitialJson(JSON.stringify(loaded));
    }
  }, [props.config, props.flag]);

  const handleChange = (field, value) => setForm((f) => ({ ...f, [field]: value }));

  const close = (refresh) => {
    refreshRef.current = !!refresh;
    setOpen(false);
  };

  const handleSave = async () => {
    if (!form.numberId) {
      props.showAlert(form.matchType === 'item' ? 'נא למלא מק"ט פריט' : 'נא למלא מספר מוצר');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        numberId: form.numberId.trim(),
        matchType: form.matchType,
        description: form.description || null,
        tagId: null,
        tagIds: form.tags.map((tag) => tag.id),
        statusId: form.statusId || null,
        isActive: form.isActive,
        trackingEventType: form.trackingEventType ? form.trackingEventType.trim() : null,
        schoolerCourse: form.schoolerCourse || null,
      };
      if (isAdd) {
        await props.createCardcomConfig(payload);
        props.showAlert('התצורה נוצרה בהצלחה');
      } else {
        await props.updateCardcomConfig(props.config.id, payload);
        props.showAlert('התצורה עודכנה בהצלחה');
      }
      close(true);
    } catch (error) {
      console.error('Error saving config:', error);
      props.showAlert('שגיאה בשמירת התצורה');
    }
    setSaving(false);
  };

  const isItem = form.matchType === 'item';
  const tagOptions = toOptions(tagList.items);

  return (
    <EditDrawer
      open={open}
      width={620}
      title={isAdd ? 'תצורת Cardcom חדשה' : `${isItem ? 'פריט' : 'עמוד'} ${form.numberId}`}
      subtitle={isAdd ? null : form.description || null}
      dirty={JSON.stringify(form) !== initialJson}
      onClose={() => close(false)}
      afterClose={() => props.onClose(refreshRef.current)}
      footer={(requestClose) => (
        <>
          <Button onClick={requestClose}>ביטול</Button>
          <Button type="primary" loading={saving} onClick={handleSave}>
            שמור
          </Button>
        </>
      )}
    >
      <Section title="מתי זה פועל">
        <FormGrid>
          <Field
            label="סוג התאמה"
            full
            hint={
              isItem
                ? 'פריט: חל רק כשהפריט עם המק"ט הזה נמצא בעסקה (בכל עמוד, בנוסף להגדרת העמוד).'
                : 'עמוד תשלום: חל על כל תשלום בעמוד.'
            }
          >
            <Segmented
              block
              disabled={!isAdd}
              value={form.matchType}
              onChange={(v) => handleChange('matchType', v)}
              options={[
                { value: 'page', label: 'עמוד תשלום (Custom19)' },
                { value: 'item', label: 'פריט בעסקה (מק"ט)' },
              ]}
            />
          </Field>
          <Field
            label={isItem ? 'מק"ט פריט' : 'מספר מוצר (Number ID)'}
            required
            hint={
              !isAdd
                ? 'לא ניתן לשנות לאחר יצירה'
                : isItem
                ? 'חייב להתאים בדיוק למק"ט שהוגדר לפריט בעמוד התשלום ב-Cardcom'
                : 'חייב להתאים ל-Custom19 שנשלח מטופס Cardcom'
            }
          >
            <Input
              dir="ltr"
              disabled={!isAdd}
              value={form.numberId}
              onChange={(e) => handleChange('numberId', e.target.value)}
              placeholder={isItem ? 'upsale-item' : '28 או PROD-001'}
            />
          </Field>
          <Field label="תיאור" hint="מה מייצג המוצר (שם הקורס, המוצר וכו׳)">
            <Input value={form.description} onChange={(e) => handleChange('description', e.target.value)} placeholder="קורס פרימיום — מכירה ראשונית" />
          </Field>
        </FormGrid>
      </Section>

      <Section title="מה קורה בתשלום מוצלח">
        <FormGrid>
          <Field label="טאגים" full hint="כל הטאגים יתווספו ללקוח">
            <Select
              mode="multiple"
              allowClear
              optionFilterProp="label"
              placeholder="הוסף טאגים…"
              loading={tagList.loading}
              value={form.tags.map((t) => t.id)}
              onChange={(ids, opts) =>
                handleChange(
                  'tags',
                  ids.map((id, i) => {
                    const existing = form.tags.find((t) => t.id === id);
                    if (existing) return existing;
                    const item = (opts[i] && opts[i].item) || {};
                    return { id, name: itemLabel(item), color: item.color };
                  })
                )
              }
              options={tagOptions}
            />
          </Field>
          <Field label="סטטוס ללקוח חדש" hint="רק ללקוחות חדשים — לקוח קיים שומר על הסטטוס שלו">
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="ללא"
              value={form.statusId || undefined}
              onChange={(id, opt) => setForm((f) => ({ ...f, statusId: id || null, statusName: opt ? itemLabel(opt.item) : '' }))}
              options={toOptions(statusList.items)}
              optionRender={(opt) => <StatusBadge color={statusColor(opt.value)}>{opt.label}</StatusBadge>}
            />
          </Field>
          <Field label="הרשמה לקורס (Schooler)" hint="Schooler שולח ללקוח מייל עם הקישור לקורס">
            <Select
              allowClear
              showSearch
              placeholder="ללא הרשמה לקורס"
              value={form.schoolerCourse || undefined}
              onChange={(v) => handleChange('schoolerCourse', v || '')}
              optionFilterProp="label"
              // label = Hebrew name; the stored value stays the key (e.g. what-woman-wants)
              options={courses.map((c) => ({ value: c.key, label: c.name }))}
            />
          </Field>
          <Field label="אירוע מעקב (Event Tracking)" full hint="ריק = ללא מעקב. נפוצים: payment, checkout_complete, signup">
            <Input dir="ltr" value={form.trackingEventType} onChange={(e) => handleChange('trackingEventType', e.target.value)} placeholder="payment" />
          </Field>
        </FormGrid>
      </Section>

      <Section>
        <label className={classes.activeRow}>
          <Switch checked={form.isActive === true} onChange={(v) => handleChange('isActive', v)} />
          <span>
            <span className={classes.activeTitle}>{form.isActive ? 'התצורה פעילה' : 'התצורה כבויה'}</span>
            <span className={classes.activeHint}>תצורה כבויה לא מטפלת בתשלומים</span>
          </span>
        </label>
      </Section>

      <details className={classes.how}>
        <summary>איך זה עובד</summary>
        <ol>
          <li>
            לקוח משלם דרך Cardcom עם Custom19=<span dir="ltr">{form.numberId || 'XX'}</span>
          </li>
          <li>המערכת מזהה את מספר המוצר ומחפשת תצורה זו</li>
          <li>לקוח חדש: נוצר לקוח עם הסטטוס שהוגדר כאן</li>
          <li>לקוח קיים: רק הטאגים מתווספים (הסטטוס לא משתנה)</li>
          <li>פרטי התשלום נוספים להיסטוריית הלקוח</li>
          <li>אם הוגדר אירוע מעקב: נרשם אירוע במערכת Event Tracking (לא משפיע על התשלום)</li>
          <li>אם נבחר קורס: הלקוח נרשם לקורס ב-Schooler (כישלון נרשם בהיסטוריה ונשלחת התראה למנהל)</li>
          <li>תצורת פריט (מק"ט) עובדת בדיוק אותו דבר, רק מופעלת כשהפריט נמצא בעסקה (בכל עמוד)</li>
        </ol>
      </details>
    </EditDrawer>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      createCardcomConfig: actionsCreators.createCardcomConfig,
      updateCardcomConfig: actionsCreators.updateCardcomConfig,
      getSchoolerCourses: actionsCreators.getSchoolerCourses,
      showAlert: actionsCreators.showAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(CardcomConfigUpdater);
